/**
 * @flow
 */

import React, { type Element, type Node } from "react";

import { FontAwesomeIcon } from "./FontAwesomeIcon";

type Props = {|
  inputID: ?string,
  inputName: ?string,
  inputClasses?: string,
  name: ?string,
  value: ?string,
  placeholder?: string,
  icon: Element<*>,
  iconBackgroundColor: string,
  enabled: boolean,
  errors?: Array<string>,
  onFocusInput?: (event: SyntheticFocusEvent<HTMLInputElement>) => void,
  onClickInput?: (event: SyntheticMouseEvent<HTMLInputElement>) => void,
  onClickIcon?: (event: SyntheticMouseEvent<HTMLButtonElement>) => void,
  onClose?: () => void,
|};

const styles = {
  inputLabel: {
    marginTop: "10px",
    marginBottom: "5px",
    display: "block",
  },
  inputError: {
    marginBottom: "5px",
  },
  inputGroup: {
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  inputElement: {
    flexGrow: 1,
    height: "34px",
    zIndex: 0,
    margin: 0,
    paddingLeft: "8px",
    paddingRight: "30px",
    borderTopLeftRadius: "3px",
    borderBottomLeftRadius: "3px",
    borderTopRightRadius: "0",
    borderBottomRightRadius: "0",
    borderRight: "none", // Remove right border as button provides it
    cursor: "pointer", // Keep pointer cursor
  },
  closeButton: {
    position: "absolute",
    zIndex: 1,
    top: "0",
    right: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "34px",
    width: "30px",
    background: "none",
    border: "none",
    padding: "0",
    cursor: "pointer",
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "34px",
    borderTopRightRadius: "3px",
    borderBottomRightRadius: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    border: "1px solid",
    borderLeft: "none",
    cursor: "pointer",
    flexShrink: 0,
  },
};

export const InputWithIcon = ({
  inputID,
  inputName,
  inputClasses,
  name,
  value,
  icon,
  placeholder,
  onClickIcon,
  iconBackgroundColor,
  enabled,
  errors,
  onClickInput,
  onFocusInput,
  onClose,
}: Props): Node => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        width: "100%",
      }}
    >
      {name != null && (
        <label htmlFor={inputID || undefined} style={styles.inputLabel}>
          {name}
        </label>
      )}

      <div style={styles.inputGroup}>
        <div style={{ display: "flex", flexGrow: 1 }}>
          <input
            id={inputID}
            className={inputClasses || ""}
            style={styles.inputElement}
            type="text"
            name={name || inputName}
            value={value || ""}
            placeholder={placeholder}
            onClick={onClickInput}
            onFocus={onFocusInput}
            disabled={!enabled}
            readOnly
          />
          {onClose && value && enabled && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
              style={styles.closeButton}
            >
              <FontAwesomeIcon
                icon={{
                  style: "solid",
                  name: "times",
                }}
                size={3}
                additionalContainerClasses="date-picker-input-close"
                additionalClasses="has-text-grey"
              />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onClickIcon}
          style={{
            ...styles.iconButton,
            backgroundColor: iconBackgroundColor,
            borderColor: iconBackgroundColor,
            cursor: onClickIcon ? "pointer" : "default",
          }}
          disabled={!enabled || !onClickIcon}
          aria-label={placeholder || name}
        >
          {icon}
        </button>
      </div>
      {errors != null &&
        enabled &&
        errors.map((e) => (
          <p key={`error-${e}`} style={styles.inputError} className="help is-danger is-size-6">
            {e}
          </p>
        ))}
    </div>
  );
};
